import React, { useState } from "react";
import { ENGINES } from "@samra/shared";
import { get, post } from "../api";
import { errText, t } from "../i18n";
import { navigate, toast } from "../store";
import { Button, Empty, Field, GameArt, Modal, Spinner, useAsync } from "../ui";

export function GameLobby({ gameId }: { gameId: string }) {
  const engine = ENGINES[gameId];
  const rooms = useAsync(() => get(`/api/rooms?game=${encodeURIComponent(gameId)}`), [gameId]);
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  if (!engine) return <Empty>{t.errors.game_unavailable}</Empty>;

  const join = async (body: object) => {
    setBusy(true);
    try {
      const r = await post("/api/rooms/join", body);
      navigate(`/room/${r.room.id}`);
    } catch (e) {
      toast(errText(e), "error");
    } finally {
      setBusy(false);
    }
  };

  const quickPlay = async () => {
    const open = (rooms.data?.rooms ?? []).find((r: any) => r.status === "waiting" && r.players < r.max_players);
    if (open) return join({ roomId: open.id });
    setBusy(true);
    try {
      const max = engine.playerCounts.includes(4) ? 4 : engine.playerCounts[engine.playerCounts.length - 1];
      const r = await post("/api/rooms", { gameId, visibility: "public", maxPlayers: max, quick: true });
      navigate(`/room/${r.room.id}`);
    } catch (e) {
      toast(errText(e), "error");
    } finally {
      setBusy(false);
    }
  };

  const vsComputer = async (level: "easy" | "medium" | "hard") => {
    setBusy(true);
    try {
      const max = engine.playerCounts.includes(4) ? 4 : engine.playerCounts[engine.playerCounts.length - 1];
      const r = await post("/api/rooms", { gameId, visibility: "private", maxPlayers: max, vsComputer: true, botLevel: level });
      navigate(`/room/${r.room.id}`);
    } catch (e) {
      toast(errText(e), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lobby">
      <header className="lobby-head">
        <div className="lobby-art"><GameArt id={gameId} /></div>
        <div>
          <h1>{t.game[gameId]}</h1>
          <p className="muted">{t.gameBlurb[gameId]} · {engine.playerCounts.join("/")} {t.players}</p>
        </div>
      </header>
      <div className="lobby-actions">
        <Button onClick={quickPlay} busy={busy}>{t.quickPlay}</Button>
        <Button kind="ghost" onClick={() => setCreating(true)}>{t.createRoom}</Button>
        <div className="vs-computer" role="group" aria-label={t.vsComputer}>
          <span className="muted">{t.vsComputer}:</span>
          {(["easy", "medium", "hard"] as const).map((lv) => (
            <Button key={lv} kind="ghost" onClick={() => vsComputer(lv)} busy={busy}>{t.botLevel[lv]}</Button>
          ))}
        </div>
        <form
          className="code-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim().length === 6) void join({ code: code.trim().toUpperCase() });
          }}
        >
          <input
            aria-label={t.roomCode}
            placeholder={t.roomCode}
            value={code}
            maxLength={6}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            dir="ltr"
          />
          <Button kind="ghost" type="submit" disabled={code.trim().length !== 6}>{t.join}</Button>
        </form>
      </div>
      <section className="panel">
        <header className="panel-head">
          <h2>{t.publicRooms}</h2>
          <button className="link-btn" onClick={rooms.reload}>{t.retry}</button>
        </header>
        {rooms.loading && !rooms.data ? (
          <Spinner />
        ) : rooms.data?.rooms?.length ? (
          <ul className="list">
            {rooms.data.rooms.map((r: any) => (
              <li key={r.id} className="list-row">
                <span>
                  <strong>{r.host_name}</strong>
                  <small className="muted"> · {r.players}/{r.max_players} · {r.status === "waiting" ? t.waiting : t.playing}</small>
                </span>
                <Button kind="ghost" disabled={r.status !== "waiting" || r.players >= r.max_players} onClick={() => join({ roomId: r.id })}>
                  {t.join}
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>{t.noPublicRooms}</Empty>
        )}
      </section>
      {creating && <CreateRoom gameId={gameId} onClose={() => setCreating(false)} />}
    </div>
  );
}

function CreateRoom({ gameId, onClose }: { gameId: string; onClose: () => void }) {
  const engine = ENGINES[gameId];
  const [visibility, setVisibility] = useState<"public" | "private">("private");
  const [maxPlayers, setMax] = useState(engine.playerCounts.includes(4) ? 4 : engine.playerCounts[engine.playerCounts.length - 1]);
  const [opts, setOpts] = useState<any>({});
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={`${t.createRoom} — ${t.game[gameId]}`} onClose={onClose}>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            const r = await post("/api/rooms", { gameId, visibility, maxPlayers, options: opts });
            navigate(`/room/${r.room.id}`);
          } catch (e) {
            toast(errText(e), "error");
            setBusy(false);
          }
        }}
      >
        <div className="segmented" role="radiogroup">
          {(["private", "public"] as const).map((v) => (
            <button type="button" key={v} role="radio" aria-checked={visibility === v} className={visibility === v ? "is-on" : ""} onClick={() => setVisibility(v)}>
              {t[v]}
            </button>
          ))}
        </div>
        <div className="field" role="group" aria-label={t.players}>
          <span className="field-label">{t.players}</span>
          <div className="segmented" role="radiogroup">
            {engine.playerCounts.map((n) => (
              <button type="button" key={n} role="radio" aria-checked={maxPlayers === n} className={maxPlayers === n ? "is-on" : ""} onClick={() => setMax(n)}>
                {n}
              </button>
            ))}
          </div>
        </div>
        {gameId === "domino" && (
          <Field label={t.score}>
            <select value={opts.target ?? 100} onChange={(e) => setOpts({ ...opts, target: Number(e.target.value) })}>
              {[50, 100, 150, 200].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
        )}
        {gameId === "memory" && (
          <Field label={t.pairsFound}>
            <select value={opts.pairs ?? 12} onChange={(e) => setOpts({ ...opts, pairs: Number(e.target.value) })}>
              {[8, 12, 16].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
        )}
        <Button type="submit" busy={busy}>{t.createRoom}</Button>
      </form>
    </Modal>
  );
}
